import { rgba } from 'polished';

export const styles = theme => ({
  overlay: {
    position: 'fixed',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: 1100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    backgroundColor: rgba(theme.color.background.dark, 0.85),
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    opacity: 0
  },
  modal: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 720,
    maxHeight: 'min(82vh, 640px)',
    borderRadius: 14,
    border: '1px solid ' + rgba(theme.color.secondary.main, 0.4),
    backgroundColor: rgba(theme.color.background.dark, 0.96),
    boxShadow: `0 16px 50px rgba(0, 0, 0, 0.85), 0 0 24px ${rgba(theme.color.secondary.main, 0.2)}`,
    overflow: 'hidden',
    opacity: 0
  },
  terminalBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: [10, 22],
    borderBottom: '1px solid ' + rgba(theme.color.secondary.dark, 0.16),
    backgroundColor: rgba(theme.color.background.dark, 0.5),
    userSelect: 'none'
  },
  terminalStatus: {
    color: rgba(theme.color.secondary.main, 0.6),
    fontSize: 11,
    letterSpacing: '0.1em',
    fontFamily: theme.typography.secondary,
    textTransform: 'uppercase'
  },
  header: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: [24, 32],
    borderBottom: '1px solid ' + rgba(theme.color.secondary.dark, 0.22),
    backgroundColor: rgba(theme.color.secondary.main, 0.03)
  },
  prompt: {
    color: theme.color.secondary.main,
    fontFamily: theme.typography.primary,
    fontSize: 24,
    fontWeight: 'bold',
    marginRight: 20,
    userSelect: 'none',
    textShadow: `0 0 6px ${theme.color.secondary.main}`
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: theme.color.primary.main,
    fontFamily: theme.typography.primary,
    fontSize: 19,
    letterSpacing: 0.6,
    caretColor: theme.color.secondary.main,

    '&::placeholder': {
      color: rgba(theme.color.text.main, 0.4),
      fontFamily: theme.typography.primary
    }
  },
  results: {
    position: 'relative',
    flex: 1,
    overflowY: 'auto',
    padding: [20, 26],
    display: 'flex',
    flexDirection: 'column',

    '&::-webkit-scrollbar': {
      width: 5
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: rgba(0, 0, 0, 0.4)
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: rgba(theme.color.secondary.main, 0.35)
    }
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 10,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.2),
    padding: [18, 26],
    marginBottom: 14,
    backgroundColor: rgba(theme.color.background.dark, 0.45),
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'border-color 0.14s ease-out, background-color 0.14s ease-out, box-shadow 0.14s ease-out, transform 0.14s ease-out',

    '&:last-child': {
      marginBottom: 0
    },

    '&:hover, &$itemActive': {
      borderColor: rgba(theme.color.secondary.main, 0.75),
      backgroundColor: rgba(theme.color.secondary.main, 0.12),
      boxShadow: `0 0 16px ${rgba(theme.color.secondary.main, 0.16)}`,
      transform: 'translateX(3px)'
    }
  },
  itemActive: {},
  itemLeft: {
    minWidth: 0,
    flex: 1,
    marginRight: 28
  },
  itemTitle: {
    margin: 0,
    color: theme.color.primary.main,
    fontSize: 15,
    lineHeight: 1.3,
    fontFamily: theme.typography.primary,
    textShadow: `0 0 4px ${rgba(theme.color.secondary.main, 0.4)}`
  },
  itemSubtitle: {
    margin: [6, 0, 0],
    color: theme.color.text.main,
    fontSize: 12,
    lineHeight: 1.4,
    opacity: 0.8,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  itemRight: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    flexShrink: 0
  },
  categoryBadge: {
    borderRadius: 5,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.45),
    padding: [6, 14],
    marginRight: 20,
    color: theme.color.secondary.main,
    fontSize: 11,
    lineHeight: 1.2,
    fontFamily: theme.typography.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    backgroundColor: rgba(theme.color.secondary.main, 0.08)
  },
  enterBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 5,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.4),
    padding: [6, 13],
    color: theme.color.secondary.main,
    fontSize: 12,
    fontFamily: theme.typography.primary,
    backgroundColor: rgba(theme.color.secondary.main, 0.08)
  },
  arrowIcon: {
    color: theme.color.secondary.main,
    fontSize: 15,
    lineHeight: 1,
    opacity: 0.85
  },
  empty: {
    padding: [28, 16],
    textAlign: 'center',
    color: rgba(theme.color.text.main, 0.6),
    fontFamily: theme.typography.primary,
    fontSize: 14
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: [18, 30],
    borderTop: '1px solid ' + rgba(theme.color.secondary.dark, 0.22),
    backgroundColor: rgba(0, 0, 0, 0.5),
    color: rgba(theme.color.text.main, 0.65),
    fontSize: 12,
    fontFamily: theme.typography.secondary,
    userSelect: 'none'
  },
  shortcuts: {
    display: 'flex',
    alignItems: 'center'
  },
  shortcutGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    marginRight: 40,

    '&:last-child': {
      marginRight: 0
    }
  },
  shortcutKey: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.45),
    padding: [5, 11],
    marginRight: 8,
    borderRadius: 4,
    color: theme.color.secondary.main,
    fontSize: 11,
    lineHeight: 1,
    fontFamily: theme.typography.primary,
    backgroundColor: rgba(theme.color.secondary.main, 0.08)
  },
  shortcutLabel: {
    color: rgba(theme.color.text.main, 0.75),
    fontSize: 12,
    letterSpacing: '0.04em',
    marginLeft: 8
  },
  resultsCount: {
    color: rgba(theme.color.text.main, 0.6),
    fontSize: 12,
    letterSpacing: '0.04em'
  }
});
